/**
 * Episode identity and ordering.
 *
 * One formatter, imported by every screen. Showing an episode as "S04E11" in
 * one place and "4x11" in another was a real defect in the prototype, and the
 * fix is that no screen formats a code itself.
 */
import type { LibraryShow } from "@/api/schemas";

/** Simkl returns `next_to_watch` and `last_watched` pre-formatted as "S07E06". */
const CODE = /^S(\d+)E(\d+)$/iu;

export interface EpisodeRef {
  season: number;
  episode: number;
}

export function parseCode(code: string | null): EpisodeRef | null {
  if (code === null) return null;
  const match = CODE.exec(code);
  if (!match?.[1] || !match[2]) return null;
  return { season: Number(match[1]), episode: Number(match[2]) };
}

export function formatCode(ref: EpisodeRef): string {
  const season = String(ref.season).padStart(2, "0");
  const episode = String(ref.episode).padStart(2, "0");
  return `S${season}E${episode}`;
}

/** Episodes aired but not yet watched. Never negative when Simkl data disagrees. */
export function remaining(show: LibraryShow): number {
  const aired = show.totalEpisodes - show.notAiredEpisodes;
  return Math.max(0, aired - show.watchedEpisodes);
}

export interface QueueEntry {
  show: LibraryShow;
  code: string;
  remaining: number;
}

/**
 * The Up Next queue: one entry per show with something left to watch.
 *
 * R7 asks for chronological order by the next episode's air date. The library
 * endpoint does not carry air dates, and fetching them per show would be one
 * request each across 80 shows. Until U9 brings the calendar in, the order is
 * most-recently-watched first, which keeps a show you are actively working
 * through at the top. This is an interim ordering, not the final one.
 */
export function buildQueue(shows: readonly LibraryShow[]): QueueEntry[] {
  const entries: QueueEntry[] = [];
  for (const show of shows) {
    if (show.nextToWatch === null) continue;
    // nextToWatch alone is not enough: Simkl keeps pointing at an episode for
    // shows that are caught up or finished, so a show with nothing aired-and-
    // unwatched would appear in the queue reading "0 episodes left".
    const left = remaining(show);
    if (left === 0) continue;
    entries.push({ show, code: show.nextToWatch, remaining: left });
  }
  entries.sort((a, b) => {
    const left = a.show.lastWatchedAt ?? a.show.addedAt ?? "";
    const right = b.show.lastWatchedAt ?? b.show.addedAt ?? "";
    if (left === right) return a.show.show.title.localeCompare(b.show.show.title);
    return right.localeCompare(left);
  });
  return entries;
}
